import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'comparisonCharts',
    'resultsContent',
    'generateBtn',
    'shareModal',
    'shareLink'
  ]

  static values = {
    experimentId: String,
    accountId: String
  }

  connect() {
    if (this.hasComparisonChartsTarget) {
      this.loadComparisonData()
    }
  }

  async loadComparisonData() {
    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}/comparison`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) throw new Error('Failed to load comparison data')

      const data = await response.json()
      this.renderComparisonCharts(data)
    } catch (error) {
      console.error('Error loading comparison:', error)
      this.comparisonChartsTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>Unable to load comparison data.
            <button class="text-blue-600 hover:underline"
              data-action="click->dashboard--experiment-show#loadComparisonData">Try again</button>
          </p>
        </div>
      `
    }
  }

  renderComparisonCharts(data) {
    if (!data.comparison || data.comparison.metrics.length === 0) {
      this.comparisonChartsTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>Not enough data to show comparison. Keep tracking your metrics!</p>
        </div>
      `
      return
    }

    const metricsHtml = data.comparison.metrics.map((metric) => `
      <div class="p-4 border border-gray-200 bg-gray-50">
        <h4 class="font-medium mb-3">${this.formatMetricName(metric.metric_type)}</h4>
        <div class="grid grid-cols-3 gap-4 text-center">
          <div>
            <span class="block text-sm text-gray-500">Baseline</span>
            <span class="block text-lg font-bold">${metric.baseline_mean?.toFixed(2) || 'N/A'}</span>
          </div>
          <div>
            <span class="block text-sm text-gray-500">Experiment</span>
            <span class="block text-lg font-bold">${metric.experiment_mean?.toFixed(2) || 'N/A'}</span>
          </div>
          <div>
            <span class="block text-sm text-gray-500">Change</span>
            <span class="block text-lg font-bold ${this.getChangeClass(metric.change_percent)}">
              ${metric.change_percent > 0 ? '+' : ''}${metric.change_percent?.toFixed(1) || 0}%
            </span>
          </div>
        </div>
        ${metric.statistically_significant ?
    '<span class="inline-block mt-2 text-xs px-2 py-1 bg-green-100 text-green-800">Significant</span>' :
    ''}
      </div>
    `).join('')

    this.comparisonChartsTarget.innerHTML = `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        ${metricsHtml}
      </div>
    `
  }

  getChangeClass(changePercent) {
    if (changePercent > 0) return 'text-green-600'
    if (changePercent < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  formatMetricName(metricType) {
    return metricType.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  refreshComparison() {
    this.comparisonChartsTarget.innerHTML = `
      <div class="flex items-center justify-center h-64 text-gray-500">
        <svg class="w-8 h-8 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0
               3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading comparison data...
      </div>
    `
    this.loadComparisonData()
  }

  async startBaseline(event) {
    if (!confirm('Start the baseline period? You should have your variables and metrics configured before starting.')) {
      return
    }

    await this.performAction('start_baseline', 'Starting baseline...', event)
  }

  async startExperiment(event) {
    if (!confirm('Start the experiment period? Make sure you have enough baseline data for comparison.')) {
      return
    }

    await this.performAction('start_experiment', 'Starting experiment...', event)
  }

  async completeExperiment(event) {
    if (!confirm('Complete this experiment? This will generate the final analysis.')) {
      return
    }

    await this.performAction('complete', 'Completing experiment...', event)
  }

  async archiveExperiment() {
    if (!confirm('Archive this experiment? You can still view it but it will be hidden from your active list.')) {
      return
    }

    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) throw new Error('Failed to archive experiment')

      this.showNotification('Experiment archived successfully', 'success')
      window.location.href = '/dashboard/experiments'
    } catch (error) {
      console.error('Error archiving:', error)
      this.showNotification('Failed to archive experiment', 'error')
    }
  }

  async performAction(action, loadingMessage, event) {
    const button = event?.target || event?.currentTarget
    const originalText = button?.textContent

    if (button) {
      button.disabled = true
      button.textContent = loadingMessage
    }

    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Action failed')
      }

      this.showNotification('Success!', 'success')
      window.location.reload()
    } catch (error) {
      console.error(`Error performing ${action}:`, error)
      this.showNotification(error.message || 'Action failed', 'error')
      if (button) {
        button.disabled = false
        button.textContent = originalText
      }
    }
  }

  async generateResults() {
    if (!this.hasGenerateBtnTarget) return

    this.generateBtnTarget.disabled = true
    this.generateBtnTarget.textContent = 'Generating...'

    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}/results/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ result_type: 'summary' })
      })

      if (!response.ok) throw new Error('Failed to generate results')

      this.showNotification('Analysis generation started. It will appear here when ready.', 'success')
    } catch (error) {
      console.error('Error generating results:', error)
      this.showNotification('Failed to generate analysis', 'error')
    } finally {
      this.generateBtnTarget.disabled = false
      this.generateBtnTarget.textContent = 'Generate Summary'
    }
  }

  openShareModal() {
    this.shareModalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
  }

  closeShareModal() {
    this.shareModalTarget.classList.add('hidden')
    document.body.style.overflow = ''
  }

  async createShare(event) {
    event.preventDefault()

    const form = event.target
    const formData = new FormData(form)

    const payload = {
      password: formData.get('password') || null,
      expires_at: formData.get('expires_at') || null,
      show_variables: formData.get('show_variables') === 'on',
      show_raw_data: formData.get('show_raw_data') === 'on',
      allow_comments: formData.get('allow_comments') === 'on'
    }

    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}/shares`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) throw new Error('Failed to create share link')

      const data = await response.json()
      this.showNotification('Share link created!', 'success')
      this.closeShareModal()

      // Copy to clipboard
      await navigator.clipboard.writeText(data.share_url)
      this.showNotification('Link copied to clipboard', 'success')

      // Reload to show new share link
      window.location.reload()
    } catch (error) {
      console.error('Error creating share:', error)
      this.showNotification('Failed to create share link', 'error')
    }
  }

  async copyShareLink(event) {
    const linkInput = event.target.closest('div').querySelector('input[type="text"]')
    if (!linkInput) return

    try {
      await navigator.clipboard.writeText(linkInput.value)
      this.showNotification('Link copied to clipboard', 'success')
    } catch (error) {
      linkInput.select()
      document.execCommand('copy')
      this.showNotification('Link copied to clipboard', 'success')
    }
  }

  async revokeShare(event) {
    const shareId = event.target.dataset.shareId
    if (!shareId) return

    if (!confirm('Revoke this share link? Anyone with this link will no longer be able to view the experiment.')) {
      return
    }

    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}/shares/${shareId}/revoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) throw new Error('Failed to revoke share')

      this.showNotification('Share link revoked', 'success')
      window.location.reload()
    } catch (error) {
      console.error('Error revoking share:', error)
      this.showNotification('Failed to revoke share link', 'error')
    }
  }

  async exportGraphic() {
    this.showNotification('Generating graphic...', 'success')

    try {
      const response = await fetch(`/api/v1/experiments/${this.experimentIdValue}/export`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) throw new Error('Failed to export graphic')

      const data = await response.json()

      // Open the HTML graphic in a new tab
      if (data.html) {
        const newWindow = window.open('', '_blank')
        newWindow.document.write(data.html)
        newWindow.document.close()
      }
    } catch (error) {
      console.error('Error exporting:', error)
      this.showNotification('Failed to generate graphic', 'error')
    }
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 border-2 ${
      type === 'success' ?
        'bg-green-50 border-green-500 text-green-700' :
        'bg-red-50 border-red-500 text-red-700'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
